import { useEffect, useState } from 'react';
import { useChat } from '../store';
import { api } from '../api';
import { Avatar } from './Avatar';
import {
  IconArchive,
  IconChat,
  IconCheckDouble,
  IconLogout,
  IconPhone,
  IconPlus,
  IconSettings,
} from './Icon';
import type { Channel } from '../types';

export type NavView = 'chats' | 'calls' | 'archived';

interface IconNavProps {
  view: NavView;
  onView: (view: NavView) => void;
  onOpenSettings: () => void;
  onLogout: () => void;
  onCreateChannel: () => void;
  onOpenChannelSettings: (channelId: string) => void;
}

interface NavItem {
  id: NavView;
  label: string;
  icon: typeof IconChat;
  badge?: number;
}

export function IconNav({
  view,
  onView,
  onOpenSettings,
  onLogout,
  onCreateChannel,
  onOpenChannelSettings,
}: IconNavProps) {
  const conversations = useChat((s) => s.conversations);
  const channels = useChat((s) => s.channels);
  const activeChannelId = useChat((s) => s.activeChannelId);
  const setActiveChannelId = useChat((s) => s.setActiveChannelId);
  const setChannels = useChat((s) => s.setChannels);
  const unreadCount = conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0);

  const items: NavItem[] = [
    {
      id: 'chats',
      label: 'Direct messages',
      icon: IconChat,
      badge: unreadCount > 0 ? unreadCount : undefined,
    },
    { id: 'calls', label: 'Calls', icon: IconPhone },
    { id: 'archived', label: 'Archived', icon: IconArchive },
  ];

  // Drag-to-reorder state. dragId is the channel being dragged; dragOverId
  // is the row currently hovered. The drop handler shuffles the local
  // order optimistically and writes the new order to the server; if the
  // server rejects, we silently re-fetch.
  const [dragId, setDragId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  function reorder(fromId: string, toId: string) {
    if (fromId === toId) return;
    const fromIdx = channels.findIndex((c) => c.id === fromId);
    const toIdx = channels.findIndex((c) => c.id === toId);
    if (fromIdx === -1 || toIdx === -1) return;
    const next = [...channels];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved!);
    // Optimistic local update — fix up sortOrder values so the icon-nav
    // re-renders in the new order. The server rewrites canonical
    // sort_order in its reorder endpoint anyway.
    const reindexed = next.map((c, i) => ({ ...c, sortOrder: i }));
    setChannels(reindexed);
    void api
      .reorderChannels(reindexed.map((c) => c.id))
      .catch(() =>
        // Drop back to server truth on any failure.
        api.listChannels().then((r) => setChannels(r.channels)),
      );
  }

  // Per-channel unread = sum of unread counts on conversations whose
  // channelId matches.
  function unreadFor(channelId: string): number {
    let n = 0;
    for (const c of conversations) {
      if (c.channelId === channelId) n += c.unreadCount ?? 0;
    }
    return n;
  }

  return (
    <nav className="w-16 shrink-0 hairline bg-zinc-950/40 flex flex-col items-center py-4">
      <div className="flex-1 flex flex-col gap-1.5 overflow-y-auto items-center w-full">
        {items.map((item) => {
          const active = view === item.id && (item.id !== 'chats' || activeChannelId === null);
          const Icon = item.icon;
          return (
            <NavButton
              key={item.id}
              active={active}
              label={item.label}
              badge={item.badge}
              onClick={() => {
                onView(item.id);
                if (item.id === 'chats') setActiveChannelId(null);
              }}
            >
              <Icon size={19} strokeWidth={1.5} />
            </NavButton>
          );
        })}

        {/* Divider between top-level nav and the channels list. */}
        {channels.length > 0 && (
          <span
            aria-hidden
            className="my-2 w-7 h-px bg-gradient-to-r from-transparent via-white/[0.12] to-transparent"
          />
        )}

        {/* Channel circles. The default channel ("General") sorts to the
            top via its sort_order=0 and acts as the landing page when
            picked. */}
        {channels.map((c) => (
          <ChannelCircle
            key={c.id}
            channel={c}
            active={view === 'chats' && activeChannelId === c.id}
            unread={unreadFor(c.id)}
            isDragging={dragId === c.id}
            isDragOver={dragOverId === c.id}
            onClick={() => {
              onView('chats');
              setActiveChannelId(c.id);
            }}
            onSettings={() => onOpenChannelSettings(c.id)}
            onDragStart={() => setDragId(c.id)}
            onDragOver={(e) => {
              if (!dragId || dragId === c.id) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              setDragOverId(c.id);
            }}
            onDragLeave={() => {
              if (dragOverId === c.id) setDragOverId(null);
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (dragId) reorder(dragId, c.id);
              setDragId(null);
              setDragOverId(null);
            }}
            onDragEnd={() => {
              setDragId(null);
              setDragOverId(null);
            }}
          />
        ))}

        {/* Add-channel button below the channels list. Workspace members
            create their own channels — no admin gate needed. */}
        <button
          onClick={onCreateChannel}
          title="Create channel"
          aria-label="Create channel"
          className="group relative w-11 h-11 mt-1 rounded-full border border-dashed border-zinc-700 text-zinc-500 hover:text-zinc-200 hover:border-zinc-500 transition-all flex items-center justify-center"
        >
          <IconPlus size={14} />
        </button>
      </div>

      <div className="flex flex-col gap-1.5 pt-3 mt-3 relative">
        {/* Hairline divider above the bottom cluster. */}
        <span
          aria-hidden
          className="absolute -top-px left-2 right-2 h-px bg-gradient-to-r from-transparent via-white/[0.08] to-transparent"
        />
        <NavButton onClick={onOpenSettings} label="Settings">
          <IconSettings size={19} strokeWidth={1.5} />
        </NavButton>
        <NavButton onClick={onLogout} label="Sign out">
          <IconLogout size={19} strokeWidth={1.5} />
        </NavButton>
      </div>
    </nav>
  );
}

interface NavButtonProps {
  active?: boolean;
  label: string;
  badge?: number;
  onClick: () => void;
  children: React.ReactNode;
}

function NavButton({ active, label, badge, onClick, children }: NavButtonProps) {
  return (
    <button
      onClick={onClick}
      title={label}
      aria-label={label}
      className={`group relative w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-300 ease-spring active:scale-[0.94] ${
        active
          ? 'bg-zinc-900 text-zinc-50 surface-raised'
          : 'text-zinc-500 hover:text-zinc-100 hover:bg-zinc-900/60'
      }`}
    >
      {/* Active indicator — fluid pill on the left edge. */}
      {active && (
        <span
          aria-hidden
          className="absolute -left-2 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-full bg-gradient-to-b from-indigo-400 to-indigo-500"
        />
      )}
      <span
        className={`transition-transform duration-300 ease-spring ${
          active ? 'scale-100' : 'group-hover:scale-110'
        }`}
      >
        {children}
      </span>
      {badge !== undefined && (
        <span className="absolute top-1 right-1 bg-emerald-500 text-zinc-950 text-[9px] font-semibold tracking-tight rounded-full px-1.5 min-w-[16px] h-[16px] flex items-center justify-center shadow-[0_0_0_2px_rgb(var(--zinc-950))]">
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}

interface ChannelCircleProps {
  channel: Channel;
  active: boolean;
  unread: number;
  isDragging: boolean;
  isDragOver: boolean;
  onClick: () => void;
  onSettings: () => void;
  onDragStart: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
  onDragEnd: () => void;
}

function ChannelCircle({
  channel,
  active,
  unread,
  isDragging,
  isDragOver,
  onClick,
  onSettings,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
}: ChannelCircleProps) {
  const me = useChat((s) => s.me);
  const conversations = useChat((s) => s.conversations);
  const setConversations = useChat((s) => s.setConversations);
  const setChannels = useChat((s) => s.setChannels);
  const activeChannelId = useChat((s) => s.activeChannelId);
  const setActiveChannelId = useChat((s) => s.setActiveChannelId);
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!menuPos) return;
    const close = () => setMenuPos(null);
    window.addEventListener('click', close);
    window.addEventListener('contextmenu', close);
    window.addEventListener('blur', close);
    return () => {
      window.removeEventListener('click', close);
      window.removeEventListener('contextmenu', close);
      window.removeEventListener('blur', close);
    };
  }, [menuPos]);

  async function markAllRead() {
    setMenuPos(null);
    const targets = conversations.filter(
      (c) => c.channelId === channel.id && c.unreadCount > 0 && c.lastMessage,
    );
    setConversations(
      conversations.map((c) =>
        c.channelId === channel.id ? { ...c, unreadCount: 0 } : c,
      ),
    );
    await Promise.all(
      targets.map((c) => api.markRead(c.id, c.lastMessage!.id).catch(() => {})),
    );
  }

  async function leave() {
    setMenuPos(null);
    if (!me) return;
    if (!window.confirm(`Leave "${channel.name}"? You can be re-added later.`)) return;
    try {
      await api.removeChannelMember(channel.id, me.id);
      if (activeChannelId === channel.id) setActiveChannelId(null);
      const r = await api.listChannels();
      setChannels(r.channels);
    } catch {
      /* server enforces; ignore */
    }
  }

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', channel.id);
        onDragStart();
      }}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const MW = 200,
          MH = 150;
        const x = Math.max(8, Math.min(e.clientX, window.innerWidth - MW - 8));
        const y = Math.max(8, Math.min(e.clientY, window.innerHeight - MH - 8));
        setMenuPos({ x, y });
      }}
      className={`relative ${isDragging ? 'opacity-40' : ''} ${
        isDragOver ? 'ring-2 ring-indigo-400/60 rounded-full' : ''
      }`}
    >
      <button
        onClick={onClick}
        title={channel.name}
        aria-label={`Channel: ${channel.name}`}
        className={`relative w-11 h-11 rounded-full transition-all duration-300 ease-spring active:scale-[0.94] flex items-center justify-center ${
          active ? 'ring-2 ring-indigo-400 ring-offset-2 ring-offset-zinc-950' : ''
        }`}
      >
        {/* Active indicator — same fluid pill as the top nav. */}
        {active && (
          <span
            aria-hidden
            className="absolute -left-2 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-full bg-gradient-to-b from-indigo-400 to-indigo-500"
          />
        )}
        <Avatar
          id={channel.id}
          displayName={channel.name}
          email={channel.name}
          size={40}
          kind="channel"
          avatarUpdatedAt={channel.avatarUpdatedAt}
          emoji={channel.iconEmoji}
        />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-emerald-500 text-zinc-950 text-[9px] font-semibold tabular-nums rounded-full px-1.5 min-w-[16px] h-[16px] flex items-center justify-center shadow-[0_0_0_2px_rgb(var(--zinc-950))]">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {menuPos && (
        <div
          role="menu"
          onClick={(e) => e.stopPropagation()}
          className="fixed z-50 min-w-[12rem] glass-chrome hairline rounded-xl shadow-elevation overflow-hidden animate-scale-in py-1"
          style={{ left: menuPos.x, top: menuPos.y }}
        >
          <button
            onClick={() => {
              setMenuPos(null);
              onSettings();
            }}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-xs text-zinc-200 hover:bg-zinc-800 transition-colors"
          >
            <IconSettings size={13} className="text-zinc-400" />
            Channel settings
          </button>
          <button
            onClick={markAllRead}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-xs text-zinc-200 hover:bg-zinc-800 transition-colors"
          >
            <IconCheckDouble size={13} className="text-zinc-400" />
            Mark all read
          </button>
          <button
            onClick={leave}
            className="w-full flex items-center gap-2.5 px-3 py-2 text-left text-xs text-rose-300 hover:bg-rose-500/10 transition-colors"
          >
            <IconLogout size={13} className="text-rose-400" />
            Leave channel
          </button>
        </div>
      )}
    </div>
  );
}
